import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LogoDefault from "../../../../../components/logos/logo.svg";
import { compressImageBlob, createObjectUrlFromBlob } from "../../../../../utility/compressImage";

const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/i;

export const DEFAULT_TICKET_EMAIL_CUSTOMIZATION = {
  enabled: false,
  subject: "",
  title: "",
  message: "",
  headerImageUrl: null,
  primaryColor: "#4200ff",
  secondaryColor: "#ff02c7",
  buttonLabel: "",
  footerText: "",
};

const normalizeCustomization = (customization = {}) => ({
  ...DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
  ...customization,
  headerImageUrl: customization?.headerImageUrl || null,
});

function EmailCustomizationModal({ event, isOpen, onClose, onSave }) {
  const { t } = useTranslation("ticketingCreateEdit");
  const fileInputRef = useRef(null);
  const [draft, setDraft] = useState(normalizeCustomization(event?.ticketEmailCustomization));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setDraft(normalizeCustomization(event?.ticketEmailCustomization));
      setErrors({});
    }
  }, [event?.ticketEmailCustomization, isOpen]);

  if (!isOpen) return null;

  const updateDraft = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: null }));
  };

  const handleImageChange = async (changeEvent) => {
    const file = changeEvent.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((current) => ({ ...current, headerImageUrl: t("ticketEmailImageInvalid") }));
      return;
    }

    try {
      const compressedImage = await compressImageBlob(file, {
        maxWidth: 1200,
        maxHeight: 520,
        maxBytes: 140 * 1024,
      });
      updateDraft("headerImageUrl", createObjectUrlFromBlob(compressedImage));
    } catch (error) {
      console.error("Failed to compress email header image:", error);
      updateDraft("headerImageUrl", URL.createObjectURL(file));
    }
  };

  const validateAndSave = () => {
    const nextErrors = {};
    if (draft.enabled) {
      if (!draft.subject.trim()) nextErrors.subject = t("ticketEmailSubjectRequired");
      if (!draft.title.trim()) nextErrors.title = t("ticketEmailTitleRequired");
      if (!draft.message.trim()) nextErrors.message = t("ticketEmailMessageRequired");
      if (!HEX_COLOR_REGEX.test(draft.primaryColor)) nextErrors.primaryColor = t("invalidHexColor");
      if (!HEX_COLOR_REGEX.test(draft.secondaryColor)) nextErrors.secondaryColor = t("invalidHexColor");
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      ...draft,
      subject: draft.subject.trim(),
      title: draft.title.trim(),
      message: draft.message.trim(),
      buttonLabel: draft.buttonLabel.trim(),
      footerText: draft.footerText.trim(),
      primaryColor: draft.primaryColor.trim(),
      secondaryColor: draft.secondaryColor.trim(),
    });
  };

  const previewTitle = draft.title || event?.name || t("ticketEmailPreviewTitleFallback");
  const previewMessage = draft.message || t("ticketEmailPreviewMessageFallback");
  const previewButton = draft.buttonLabel || t("ticketEmailDefaultButtonLabel");
  const primaryColor = HEX_COLOR_REGEX.test(draft.primaryColor)
    ? draft.primaryColor
    : DEFAULT_TICKET_EMAIL_CUSTOMIZATION.primaryColor;
  const secondaryColor = HEX_COLOR_REGEX.test(draft.secondaryColor)
    ? draft.secondaryColor
    : DEFAULT_TICKET_EMAIL_CUSTOMIZATION.secondaryColor;

  return (
    <div className="ticketingSettingsModalOverlay emailCustomizationOverlay">
      <div className="ticketingSettingsModal emailCustomizationModal">
        <div className="ticketingSettingsModalHeader">
          <div>
            <p>{t("ticketEmailCustomizationEyebrow")}</p>
            <h2>{t("ticketEmailCustomizationTitle")}</h2>
            <span>{t("ticketEmailCustomizationDescription")}</span>
          </div>
          <button type="button" onClick={onClose} aria-label={t("closeModal")}>
            ×
          </button>
        </div>

        <div className="ticketingSettingsModalBody emailCustomizationBody">
          <section className="emailCustomizationEnableCard">
            <div>
              <h3>{t("ticketEmailEnableTitle")}</h3>
              <p>{t("ticketEmailEnableDescription")}</p>
            </div>
            <div className="buttonsContainer">
              <button
                type="button"
                className={draft.enabled ? "active" : ""}
                onClick={() => updateDraft("enabled", true)}
              >
                {t("yes")}
              </button>
              <button
                type="button"
                className={!draft.enabled ? "active" : ""}
                onClick={() => updateDraft("enabled", false)}
              >
                {t("no")}
              </button>
            </div>
          </section>

          <div className="emailCustomizationGrid">
            <section className="emailCustomizationEditor">
              <label>
                {t("ticketEmailSubjectLabel")}
                <input
                  value={draft.subject}
                  onChange={(event) => updateDraft("subject", event.target.value)}
                  placeholder={t("ticketEmailSubjectPlaceholder")}
                />
                {errors.subject && <span className="fieldError">{errors.subject}</span>}
              </label>

              <label>
                {t("ticketEmailTitleLabel")}
                <input
                  value={draft.title}
                  onChange={(event) => updateDraft("title", event.target.value)}
                  placeholder={event?.name || t("ticketEmailTitlePlaceholder")}
                />
                {errors.title && <span className="fieldError">{errors.title}</span>}
              </label>

              <label>
                {t("ticketEmailMessageLabel")}
                <textarea
                  value={draft.message}
                  onChange={(event) => updateDraft("message", event.target.value)}
                  placeholder={t("ticketEmailMessagePlaceholder")}
                />
                {errors.message && <span className="fieldError">{errors.message}</span>}
              </label>

              <div className="emailCustomizationImageControl">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
                <div>
                  <span>{t("ticketEmailHeaderImageLabel")}</span>
                  <small>{t("ticketEmailHeaderImageHelp")}</small>
                  {errors.headerImageUrl && <em>{errors.headerImageUrl}</em>}
                </div>
                <div className="emailCustomizationImageActions">
                  <button type="button" onClick={() => fileInputRef.current?.click()}>
                    {t("ticketEmailChooseImage")}
                  </button>
                  {draft.headerImageUrl && (
                    <button type="button" onClick={() => updateDraft("headerImageUrl", null)}>
                      {t("reset")}
                    </button>
                  )}
                </div>
              </div>

              <div className="emailCustomizationColors">
                <label>
                  {t("ticketEmailPrimaryColor")}
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(event) => updateDraft("primaryColor", event.target.value)}
                  />
                  <input
                    value={draft.primaryColor}
                    onChange={(event) => updateDraft("primaryColor", event.target.value)}
                    placeholder="#4200ff"
                  />
                  {errors.primaryColor && <span className="fieldError">{errors.primaryColor}</span>}
                </label>
                <label>
                  {t("ticketEmailSecondaryColor")}
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(event) => updateDraft("secondaryColor", event.target.value)}
                  />
                  <input
                    value={draft.secondaryColor}
                    onChange={(event) => updateDraft("secondaryColor", event.target.value)}
                    placeholder="#ff02c7"
                  />
                  {errors.secondaryColor && (
                    <span className="fieldError">{errors.secondaryColor}</span>
                  )}
                </label>
              </div>

              <label>
                {t("ticketEmailButtonLabel")}
                <input
                  value={draft.buttonLabel}
                  onChange={(event) => updateDraft("buttonLabel", event.target.value)}
                  placeholder={t("ticketEmailDefaultButtonLabel")}
                />
              </label>

              <label>
                {t("ticketEmailFooterLabel")}
                <textarea
                  value={draft.footerText}
                  onChange={(event) => updateDraft("footerText", event.target.value)}
                  placeholder={t("ticketEmailFooterPlaceholder")}
                />
              </label>
            </section>

            <section className="emailCustomizationPreview">
              <span>{t("ticketEmailPreview")}</span>
              <div className="emailPreviewFrame">
                <div className="emailPreviewCard">
                  {draft.headerImageUrl && (
                    <img src={draft.headerImageUrl} alt={t("ticketEmailPreviewImageAlt")} />
                  )}
                  <div
                    className="emailPreviewAccent"
                    style={{
                      background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                    }}
                  />
                  <div className="emailPreviewContent">
                    <small style={{ color: primaryColor }}>{event?.name || "Comitty"}</small>
                    <h3>{previewTitle}</h3>
                    <p>{previewMessage}</p>
                    <button
                      type="button"
                      style={{
                        background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                      }}
                    >
                      {previewButton}
                    </button>
                    <div className="emailPreviewAttachment">{t("ticketEmailAttachmentHint")}</div>
                  </div>
                  {draft.footerText && <footer>{draft.footerText}</footer>}
                </div>
              </div>
              {!draft.headerImageUrl && (
                <div className="emailPreviewLogoFallback">
                  <img src={event?.logoUrl || LogoDefault} alt="" />
                </div>
              )}
            </section>
          </div>
        </div>

        <div className="ticketingSettingsModalFooter">
          <button type="button" className="secondary" onClick={onClose}>
            {t("closeModal")}
          </button>
          <button type="button" className="primary" onClick={validateAndSave}>
            {t("ticketEmailSaveCustomization")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailCustomizationModal;
